import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * Enhanced chart data hook for real-time data management and visualization
 */
const useChartData = (options = {}) => {
  const { maxDataPoints = 100, autoHideThreshold = 30000 } = options
  
  const [chartData, setChartData] = useState(new Map())
  const [loading] = useState(false)
  const [error] = useState(null)
  const [availableFields, setAvailableFields] = useState(new Set())
  
  const isComponentMounted = useRef(true)
  const chartDataRef = useRef(new Map())
  const lastDataTimestamp = useRef(new Map())
  
  // Update ref whenever chartData changes
  useEffect(() => {
    chartDataRef.current = chartData
    isComponentMounted.current = true
    return () => { isComponentMounted.current = false }
  }, [chartData])

  // Chart visibility logic with throttling to prevent excessive recalculation
  const visibleCharts = useMemo(() => {
    const visible = new Set()
    for (const [key, data] of chartData) {
      if (data?.length > 0) {
        const lastTimestamp = lastDataTimestamp.current.get(key)
        if (!lastTimestamp || (Date.now() - lastTimestamp) <= autoHideThreshold) {
          visible.add(key)
        }
      }
    }
    return visible
  }, [chartData, autoHideThreshold])

  // Simplified data point addition with batching to reduce render frequency
  const addDataPoint = useCallback((deviceMeasurementKey, dataPoint) => {
    if (!isComponentMounted.current) return

    // Use requestAnimationFrame to batch DOM updates
    requestAnimationFrame(() => {
      setChartData(prevData => {
        const newData = new Map(prevData)
        const existing = newData.get(deviceMeasurementKey) || []
        
        const point = {
          x: dataPoint.x || Date.now(),
          y: dataPoint.y,
          ...dataPoint,
        }

        const updated = [...existing, point].slice(-maxDataPoints)
        newData.set(deviceMeasurementKey, updated)
        
        // Track timestamp for visibility
        lastDataTimestamp.current.set(deviceMeasurementKey, point.x)
        
        return newData
      })
    })
  }, [maxDataPoints])
  const processRealTimeData = useCallback((data) => {
    if (!Array.isArray(data)) return new Set()
    
    const newFields = new Set()
    data.forEach(item => {
      if (item.device && item.measurement) {
        const key = `${item.device}-${item.measurement}`
        newFields.add(key)
        
        addDataPoint(key, {
          x: new Date(item._time).getTime(),
          y: item._value,
          timestamp: item._time,
          value: item._value,
        })
      }
    })

    setAvailableFields(prev => {
      if (prev.size !== newFields.size || ![...prev].every(key => newFields.has(key))) {
        return newFields
      }
      return prev
    })

    return newFields
  }, [addDataPoint])

  const getChartData = useCallback((deviceMeasurementKey) => {
    return chartDataRef.current.get(deviceMeasurementKey) || []
  }, [])

  const updateChartData = useCallback((updates) => {
    if (!isComponentMounted.current) return

    setChartData(prevData => {
      const newData = new Map(prevData)
      
      Object.entries(updates).forEach(([key, points]) => {
        const existing = newData.get(key) || []
        const processedPoints = points.map(point => ({
          x: point.x || Date.now(),
          y: point.y,
          ...point,
        }))
        
        const updated = [...existing, ...processedPoints].slice(-maxDataPoints)
        newData.set(key, updated)
      })
      
      return newData
    })
  }, [maxDataPoints])

  const clearChartData = useCallback((deviceMeasurementKey = null) => {
    if (!isComponentMounted.current) return

    setChartData(prevData => {
      if (deviceMeasurementKey) {
        const newData = new Map(prevData)
        newData.delete(deviceMeasurementKey)
        return newData
      }
      return new Map()
    })
  }, [])

  // Simplified chart visibility functions
  const isChartVisible = useCallback((deviceMeasurementKey) => {
    return visibleCharts.has(deviceMeasurementKey)
  }, [visibleCharts])

  const updateChartVisibility = useCallback(() => {
    // Visibility is now handled by useMemo, this is for compatibility
  }, [])

  return {
    chartData,
    loading,
    error,
    getChartData,
    addDataPoint,
    updateChartData,
    clearChartData,
    isChartVisible,
    updateChartVisibility,
    processRealTimeData,
    visibleCharts: Array.from(visibleCharts),
    availableFields: Array.from(availableFields),
  }
}

export default useChartData
